import { AnchorOrLink } from '#app/utils/misc-react.tsx'
import { externalLinks } from '../external-links.tsx'
import { CloudflareEmailOff } from './cloudflare-email-off.tsx'
import { IconLink } from './icon-link.tsx'
import { GithubIcon, LinkedInIcon, RssIcon, XIcon } from './icons.tsx'
import { H4, H6 } from './typography.tsx'

function FooterLink({
	name,
	href,
	reload,
}: {
	name: string
	href: string
	reload?: boolean
}) {
	const link = (
		<li className="py-1">
			<AnchorOrLink
				prefetch={href.startsWith('http') ? undefined : 'intent'}
				href={href}
				className="text-secondary underlined hover:text-team-current focus:text-team-current inline-block text-lg whitespace-nowrap focus:outline-none"
				reload={reload}
			>
				{name}
			</AnchorOrLink>
		</li>
	)

	// Keep Cloudflare from rewriting mailto: (CSP + React hydration #418).
	if (href.startsWith('mailto:')) {
		return <CloudflareEmailOff>{link}</CloudflareEmailOff>
	}

	return link
}

function Footer() {
	return (
		<footer className="border-t border-gray-200 pt-24 pb-16 dark:border-gray-600">
			<div className="mx-10vw">
				<div className="mx-auto grid max-w-7xl gap-12 md:grid-cols-12 md:gap-8">
					<div className="md:col-span-5 lg:col-span-4">
						<H4 as="div">
							Hamid
							<span className="text-yellow-500">X</span>
						</H4>
						<p className="text-secondary mt-6 max-w-md text-xl">
							Product engineer for systems that stay fast under pressure.
						</p>
						<div className="text-secondary mt-8 flex items-center gap-4">
							<IconLink href={externalLinks.github} aria-label="GitHub">
								<GithubIcon size={28} />
							</IconLink>
							<IconLink href={externalLinks.x} aria-label="X / Twitter">
								<XIcon size={28} />
							</IconLink>
							<IconLink href={externalLinks.linkedin} aria-label="LinkedIn">
								<LinkedInIcon size={28} />
							</IconLink>
							<IconLink href={externalLinks.rss} aria-label="RSS">
								<RssIcon size={28} />
							</IconLink>
						</div>
					</div>

					<div className="grid grid-cols-2 gap-10 sm:grid-cols-3 md:col-span-7 md:gap-8 lg:col-span-8 lg:grid-cols-3">
						<div>
							<H6 as="div">Explore</H6>
							<ul className="mt-4">
								<FooterLink name="Work" href="/work" />
								<FooterLink name="Blog" href="/blog" />
								<FooterLink name="About" href="/about" />
								<FooterLink name="Resume" href="/resume" />
							</ul>
						</div>
						<div>
							<H6 as="div">Connect</H6>
							<ul className="mt-4">
								<FooterLink name="Contact" href="/contact" />
								<FooterLink name="Schedule a call" href={externalLinks.cal} />
								<FooterLink name="Email" href={externalLinks.email} />
								<FooterLink name="X / Twitter" href={externalLinks.x} />
								<FooterLink name="LinkedIn" href={externalLinks.linkedin} />
							</ul>
						</div>
						<div>
							<H6 as="div">More</H6>
							<ul className="mt-4">
								<FooterLink name="Search" href="/search" />
								<FooterLink name="Connect" href="/connect" />
								<FooterLink name="Sitemap" reload href="/sitemap.xml" />
							</ul>
						</div>
					</div>

					<div className="col-span-full flex flex-col gap-6 border-t border-gray-200 pt-10 md:flex-row md:items-center md:justify-between dark:border-gray-600">
						<p className="text-lg text-gray-500 dark:text-slate-500">
							<span>All rights reserved</span>{' '}
							<span className="block sm:inline">{`© HamidX ${new Date().getFullYear()}`}</span>
						</p>
						<img
							src="/images/brand-avatar.webp"
							alt="HamidX"
							className="h-14 w-14 rounded-full object-cover"
							width={112}
							height={112}
							loading="lazy"
							decoding="async"
						/>
					</div>
				</div>
			</div>
		</footer>
	)
}

export { Footer }
